// SEO Configuration for all pages - optimized for Google & LLM ranking

export const seoConfig = {
  home: {
    title: "Employee Retention Software | Enterprise Social Network - Meetworkplace",
    description: "Revolutionize employee retention with Meetworkplace's AI-powered enterprise social network. Foster workplace culture, boost employee engagement, and reduce turnover with team-building activities and relational analytics. Trusted platform for employee experience.",
    keywords: [
      "employee retention",
      "employee engagement",
      "enterprise social network",
      "employee experience",
      "workplace culture",
      "team-building activities",
      "employee retention software",
      "workplace analytics",
      "relational analytics",
      "AI predictive retention",
      "reduce employee turnover",
      "employee satisfaction platform",
      "corporate social network",
      "employee wellness program",
      "workplace collaboration tools",
      "employee retention strategies",
      "HR retention software",
      "employee experience platform",
      "workplace connection app",
      "employee engagement software"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://tomeetworkplace.com/#organization",
          "name": "Meetworkplace",
          "url": "https://tomeetworkplace.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://tomeetworkplace.com/logo512.png",
            "width": 512,
            "height": 512
          },
          "description": "Enterprise social network platform designed to improve employee retention through AI-powered matchmaking, workplace analytics, and team-building activities.",
          "sameAs": [
            "https://twitter.com/meetworkplace",
            "https://www.linkedin.com/company/meetworkplace",
            "https://www.facebook.com/meetworkplace"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Sales",
            "url": "https://tomeetworkplace.com/contact-sales"
          }
        },
        {
          "@type": "WebSite",
          "@id": "https://tomeetworkplace.com/#website",
          "url": "https://tomeetworkplace.com",
          "name": "Meetworkplace",
          "publisher": {
            "@id": "https://tomeetworkplace.com/#organization"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://tomeetworkplace.com/?s={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "SoftwareApplication",
          "name": "Meetworkplace",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, iOS, Android",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "250"
          },
          "description": "AI-powered enterprise social network for employee retention, engagement, and workplace culture building."
        }
      ]
    }
  },

  whyMeetworkplace: {
    title: "Why Meetworkplace | AI-Powered Employee Retention & Workplace Analytics",
    description: "Discover how Meetworkplace uses AI predictive retention, relational analytics, and workplace analytics to reduce employee turnover by up to 47%. Learn our proven approach to fostering positive work culture and improving employee satisfaction.",
    keywords: [
      "AI predictive retention",
      "relational analytics",
      "workplace analytics",
      "employee retention statistics",
      "reduce employee turnover",
      "employee satisfaction metrics",
      "workplace culture analytics",
      "HR analytics platform",
      "employee engagement metrics",
      "retention rate improvement",
      "workforce analytics",
      "employee retention ROI",
      "turnover cost reduction",
      "employee lifetime value",
      "workplace relationship analytics"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Why Meetworkplace - Revolutionary Employee Retention Platform",
      "description": "Learn how Meetworkplace's AI-powered analytics and social networking reduce employee turnover and improve workplace culture.",
      "author": {
        "@type": "Organization",
        "name": "Meetworkplace"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Meetworkplace",
        "logo": {
          "@type": "ImageObject",
          "url": "https://tomeetworkplace.com/logo512.png"
        }
      },
      "datePublished": "2024-01-01",
      "dateModified": new Date().toISOString().split('T')[0]
    }
  },

  employeeRetention: {
    title: "Employee Retention Solutions | Reduce Turnover by 47% - Meetworkplace",
    description: "Complete employee retention solutions combining AI predictive analytics, team-building activities, and workplace engagement tools. Proven strategies to improve retention rates, reduce hiring costs, and foster a positive work culture.",
    keywords: [
      "employee retention solutions",
      "reduce employee turnover",
      "employee retention strategies",
      "retention rate improvement",
      "employee turnover reduction",
      "retention management software",
      "employee retention platform",
      "workplace retention tools",
      "HR retention solutions",
      "employee attrition prevention",
      "staff retention program",
      "employee loyalty program",
      "retention analytics",
      "employee retention metrics",
      "turnover cost calculator"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Employee Retention: Complete Guide to Reducing Turnover",
      "description": "Comprehensive employee retention strategies and solutions to improve workplace culture and reduce costly employee turnover.",
      "author": {
        "@type": "Organization",
        "name": "Meetworkplace"
      }
    }
  },

  platform: {
    title: "Employee Experience Platform | Team Building & Workplace Engagement Software",
    description: "Transform employee experience with Meetworkplace's comprehensive platform featuring AI matchmaking, team-building activities, workplace collaboration tools, and employee engagement features designed to foster meaningful workplace relationships.",
    keywords: [
      "employee experience platform",
      "team-building activities",
      "workplace collaboration tools",
      "employee engagement platform",
      "workplace social network",
      "employee connection app",
      "team building software",
      "workplace engagement tools",
      "employee experience software",
      "corporate team building",
      "employee networking platform",
      "workplace community building",
      "employee collaboration software",
      "team bonding activities",
      "workplace relationship building"
    ]
  },

  enterprise: {
    title: "Enterprise Social Network | Corporate Collaboration & HR Analytics Platform",
    description: "Enterprise-grade social network platform with advanced HR analytics, workforce management tools, and comprehensive employee engagement solutions. Built for large organizations seeking to improve workplace culture and employee retention.",
    keywords: [
      "enterprise social network",
      "corporate social network",
      "enterprise collaboration platform",
      "HR analytics platform",
      "workforce management software",
      "enterprise employee engagement",
      "corporate culture platform",
      "enterprise HR software",
      "workplace analytics dashboard",
      "enterprise retention software",
      "corporate wellness platform",
      "enterprise team collaboration",
      "workforce analytics platform",
      "enterprise people analytics",
      "corporate employee experience"
    ]
  },

  solutions: {
    title: "Employee Engagement Solutions | Foster Positive Work Culture - Meetworkplace",
    description: "Comprehensive employee engagement and workplace culture solutions including team-building programs, employee wellness initiatives, and social connection tools to create a thriving workplace environment.",
    keywords: [
      "employee engagement solutions",
      "foster positive work culture",
      "workplace culture building",
      "employee wellness program",
      "team-building programs",
      "workplace culture solutions",
      "employee engagement strategies",
      "positive workplace environment",
      "workplace culture improvement",
      "employee morale solutions",
      "workplace happiness program",
      "employee satisfaction solutions",
      "culture transformation tools",
      "workplace wellness solutions",
      "employee connection solutions"
    ]
  },

  careers: {
    title: "Careers at Meetworkplace | Join Our Employee Retention Mission",
    description: "Join Meetworkplace in revolutionizing employee retention and workplace culture. Explore career opportunities in a company dedicated to improving employee experience worldwide.",
    keywords: [
      "careers in HR tech",
      "employee retention jobs",
      "HR software careers",
      "workplace culture jobs",
      "HR analytics careers",
      "employee engagement careers"
    ]
  },

  about: {
    title: "About Meetworkplace | Employee Retention & Workplace Culture Experts",
    description: "Learn about Meetworkplace's mission to transform employee retention through innovative social networking, AI-powered analytics, and workplace culture building solutions.",
    keywords: [
      "employee retention company",
      "workplace culture experts",
      "HR technology company",
      "employee engagement company",
      "workplace analytics company",
      "HR software company"
    ]
  },

  contactSales: {
    title: "Contact Sales | Get Employee Retention Demo - Meetworkplace",
    description: "Request a demo of Meetworkplace's employee retention platform. Speak with our experts about reducing turnover, improving workplace culture, and boosting employee engagement.",
    keywords: [
      "employee retention demo",
      "HR software demo",
      "employee engagement consultation",
      "workplace culture consultation",
      "retention software pricing",
      "employee retention consultation"
    ]
  },

  blog: {
    title: "Employee Retention Blog | Workplace Culture & Engagement Tips",
    description: "Expert insights on employee retention, workplace culture, team-building, and employee engagement strategies. Stay updated with latest HR trends and retention best practices.",
    keywords: [
      "employee retention blog",
      "workplace culture blog",
      "employee engagement tips",
      "HR best practices",
      "retention strategies blog",
      "workplace culture tips",
      "employee retention articles",
      "HR insights",
      "workplace analytics insights"
    ]
  },

  faqs: {
    title: "FAQ | Employee Retention & Workplace Engagement Questions",
    description: "Frequently asked questions about Meetworkplace's employee retention platform, pricing, features, and how our AI-powered analytics improve workplace culture.",
    keywords: [
      "employee retention FAQ",
      "workplace engagement FAQ",
      "HR software questions",
      "employee retention platform FAQ",
      "workplace culture questions"
    ]
  }
};
